import {cancelTokenSource} from "../../hooks/hooks";

/**
 * Parses event data to generate a structured list of event records.
 *
 * @param {Array<Object>} data - Array of raw event data objects.
 * @param {number} projectId - The ID of the project associated with these events.
 * @returns {Array<Object>} An array of structured event records.
 *
 * Each event record contains:
 * - `key`: A unique identifier for the event, derived from the 'Event Number'.
 * - `info`: Placeholder for additional information.
 * - `id_project`: The ID of the associated project.
 * - `is_external_event`: A boolean indicating if the event is external.
 * - `is_internal_event`: A boolean indicating if the event is internal.
 * - `level_event`: The level where the event is located.
 * - `building_event`: The building associated with the event.
 * - `room_event`: The room associated with the event.
 * - `id_material`: The ID of the hazardous material type.
 * - `quantity_event`: The quantity involved in the event.
 * - `id_unit`: The unit ID for the quantity measurement.
 * - `is_confirmed_event`: A boolean indicating if the event is confirmed based on analytical results.
 * - `is_visual_event`: A boolean indicating if the event was identified via visual inspection.
 * - `id_sample`: Identifies the sample (currently `null`).
 * - `is_friable_event`: A boolean indicating if the material is friable.
 * - `description_event`: Description of the material, potentially including sample number.
 * - `is_accessible_event`: A boolean indicating if the material is accessible.
 * - `is_restricted_event`: A boolean indicating if the event is restricted (currently `false`).
 * - `is_historical_event`: A boolean indicating if the event is historical (currently `false`).
 * - `id_condition`: The ID representing the condition of the material.
 *
 * Note: Some default values and indices are applied to align IDs with specific lists like `HAZMAT`, `UNITS`, and `CONDITIONS`.
 */

function EventsTemplate() {

    // const axios = useAxios();
    const source = cancelTokenSource;
    const name = "Events";

    const HAZMAT = [
        "Asbestos",
        "Lead (Paint)",
        "Lead",
        "SMF",
        "PCB",
        "ODS",
        "Inaccessible",
        "Other"
    ];

    const UNITS = [
        "sqm",
        "m",
        "lm",
        "u"
    ];

    const CONDITIONS = [
        "Poor",
        "Fair",
        "Good",
        "Not Applicable",
        "Unknown"
    ];

    const columns = [
        {
            title: "Project",
            key: "id_project",
            dataIndex: "id_project"
        },
        {
            title: "Int.",
            key: "is_internal_event",
            dataIndex: "is_internal_event",
            render: v => {
                return (v === true ? "Yes" : "No");
            }
        },
        {
            title: "Ext.",
            key: "is_external_event",
            dataIndex: "is_external_event",
            render: v => {
                return (v === true ? "Yes" : "No");
            }
        },
        {
            title: "Lv.",
            key: "level_event",
            dataIndex: "level_event",
        },
        {
            title: "Bd.",
            key: "building_event",
            dataIndex: "building_event"
        },
        {
            title: "Rm.",
            key: "room_event",
            dataIndex: "room_event"
        },
        {
            title: "Desc.",
            key: "description_event",
            dataIndex: "description_event"
        },
        {
            title: "Hazmat",
            key: "id_material",
            dataIndex: "id_material",
            render: v => {
                return (
                    HAZMAT[v - 1]
                );
            }
        },
        {
            title: "Q",
            key: "quantity_event",
            dataIndex: "quantity_event"
        },
        {
            title: "U",
            key: "id_unit",
            dataIndex: "id_unit",
            render: v => {
                return (
                    UNITS[v - 1]
                );
            }
        },
        {
            title: "Confirmed",
            key: "is_confirmed_event",
            dataIndex: "is_confirmed_event",
            render: v => {
                return (v === true ? "Yes" : "No");
            }
        },
        {
            title: "Visual",
            key: "is_visual_event",
            dataIndex: "is_visual_event",
            render: v => {
                return (v === true ? "Yes" : "No");
            }
        },
        {
            title: "Friable",
            key: "is_friable_event",
            dataIndex: "is_friable_event",
            render: v => {
                return (v === true ? "Yes" : "No");
            }
        },
        {
            title: "Accessible",
            key: "is_accessible_event",
            dataIndex: "is_accessible_event",
            render: v => {
                return (v === true ? "Yes" : "No");
            }
        },
        {
            title: "Condition",
            key: "id_condition",
            dataIndex: "id_condition",
            render: v => {
                return (
                    CONDITIONS[v - 1]
                );
            }
        }
    ];

    const parse = (data, projectId) => {
        var datasource = [];
        for (var r of data) {
            console.log(r)
            var sample = {
                key: Number(r['Event Number']),
                info: "info"
            };
            sample.id_project = projectId;
            sample.is_external_event = r["Internal / External"].toLowerCase().indexOf("external") !== -1;
            sample.is_internal_event = !sample.is_external_event;
            sample.level_event = r["Level"];
            sample.building_event = r["Building"];
            sample.room_event = r["Room"];
            sample.id_material = HAZMAT.indexOf(r["Hazard Type"].trim()) + 1;
            if (sample.id_material === 0) continue;
            sample.quantity_event = Number(r["Quantity"]) || 0;
            sample.id_unit = UNITS.indexOf(r["Units"]) + 1;
            if (sample.id_unit === 0) sample.id_unit = 1;
            sample.is_confirmed_event = r["Analytical Results"].toLowerCase().indexOf("negative") === -1;
            sample.is_visual_event = r["Sample Number"].trim() === "Visual Inspection";
            sample.id_sample = null;
            sample.is_friable_event = r["Friability"] === "Friable";
            sample.description_event = r["Material Description"].replace(/[\r\n'"]+/g, " ");
            // if (sample.is_visual_event) {
            if(sample.description_event.trim() !== "") {
                sample.description_event += ". ";
            }
            sample.description_event += r["Sample Number"]
            // }
            sample.is_accessible_event = r["Accessibility"] === "Accessible";
            sample.is_restricted_event = false;
            sample.is_historical_event = false;
            sample.id_condition = CONDITIONS.indexOf(r["Condition"]) + 1;
            if (sample.id_condition === 0) {
                sample.id_condition = 5;
            }
            console.log(sample)
            datasource.push(sample);
            console.log("Pushed to DS")
            console.log(datasource)
        }
        console.log(datasource)
        return datasource;
    };

    const apply = async (datasource, axios) => {
        var error = false;
        var len = datasource.length;
        for (var i = 0; i < len; i++) {
            var d = datasource[i];
            try {
                var response = {}
                    await axios.current.post("events", d,
                        // {cancelToken: cancelTokenSource.token}
                    )
                ;
                if (response.error === true) {
                    datasource[i].info = "danger";
                    error = true;
                    return;
                }
                datasource[i].info = "success";
            } catch (e) {
                datasource[i].info = "danger";
                error = true;
            }
        }
        return {datasource, error};
    };


    return {
        source,
        name,
        columns,
        parse,
        apply
    }
}

export default EventsTemplate;
